import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const CartDrawer = ({ isOpen, onClose, cart, onUpdateItem }) => {
  const navigate = useNavigate();
  const isEmpty = !cart?.items?.length;

  const handleCheckout = () => {
    onClose();
    navigate("/checkout");
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        className="w-full sm:max-w-md bg-nukleus-bg border-l border-white/10 p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-4 border-b border-white/10">
          <SheetTitle className="text-white flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-nukleus-violet" />
            Your Cart
            {cart?.items?.length > 0 && (
              <span className="text-sm text-white/50">
                ({cart.items.reduce((sum, item) => sum + item.quantity, 0)} items)
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full px-6 py-12">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                <ShoppingBag className="w-8 h-8 text-white/30" />
              </div>
              <p className="text-white/50 text-center mb-6">Your cart is empty</p>
              <Button
                onClick={onClose}
                className="bg-nukleus-violet hover:bg-nukleus-violet/90 text-white rounded-full"
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="px-6 py-4 space-y-4">
              {cart.items.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-4 p-4 bg-white/5 rounded-xl border border-white/5"
                  data-testid={`cart-item-${item.id}`}
                >
                  {/* Product Image */}
                  <div className="w-20 h-20 bg-nukleus-card rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
                    <img
                      src="https://customer-assets.emergentagent.com/job_focus-powder/artifacts/5l4mwejz_Hero%20shot.jpg"
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium text-sm mb-1 truncate">
                      {item.product_name}
                    </h4>
                    <p className="text-white/50 text-xs mb-2">
                      {item.variant}
                      {item.is_subscription && (
                        <span className="ml-2 text-nukleus-lilac">
                          (Subscribe & Save)
                        </span>
                      )}
                    </p>

                    <div className="flex items-center justify-between">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onUpdateItem(item.id, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                          data-testid={`cart-item-decrease-${item.id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-6 text-center text-sm text-white">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => onUpdateItem(item.id, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center rounded-md bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                          data-testid={`cart-item-increase-${item.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Price & Remove */}
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-white font-medium text-sm">
                            ${(item.price * item.quantity * (1 - item.discount_percent / 100)).toFixed(2)}
                          </p>
                          {item.discount_percent > 0 && (
                            <p className="text-white/30 text-xs line-through">
                              ${(item.price * item.quantity).toFixed(2)}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => onUpdateItem(item.id, 0)}
                          className="text-white/30 hover:text-red-400 transition-colors"
                          data-testid={`cart-item-remove-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Footer */}
        {!isEmpty && (
          <div className="border-t border-white/10 px-6 py-4 space-y-4">
            {/* Totals */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/50">Subtotal</span>
                <span className="text-white">${cart.subtotal?.toFixed(2)}</span>
              </div>
              {cart.discount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-nukleus-lilac">Subscription Savings</span>
                  <span className="text-nukleus-lilac">-${cart.discount?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-white/5">
                <span className="text-white font-medium">Total</span>
                <span className="text-white font-semibold text-lg">${cart.total?.toFixed(2)}</span>
              </div>
            </div>

            {/* Free Shipping Notice */}
            <p className="text-center text-xs text-nukleus-lilac">
              Free US shipping on all orders
            </p>

            {/* Checkout Button */}
            <Button
              onClick={handleCheckout}
              className="w-full bg-nukleus-violet hover:bg-nukleus-violet/90 text-white rounded-full py-6 font-semibold shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] transition-all"
              data-testid="checkout-btn"
            >
              Checkout — ${cart.total?.toFixed(2)}
            </Button>

            <p className="text-center text-xs text-white/30">
              Secure checkout powered by Shopify
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
